import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import App from '@/App';
import { i18n, i18nReady } from '@/i18n';
import { captureLink } from '@/lib/attribution';
import { queryClient } from '@/lib/queryClient';
import { canHydrate, revealPrerender } from '@/seo/prerender';

// Design tokens + Tailwind layers. Authored by the design agent; this is the only place
// the stylesheet is pulled in.
import './index.css';
import '@fontsource-variable/manrope';
import '@fontsource-variable/space-grotesk';
import './nexus.css';
import './aether.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root not found in index.html');
}

// docs/BILLING.md §9 — a coupon in the link (`?c=CODE`), kept for the checkout, and its campaign
// tags (`utm_*`, `ref`), kept for the sign-up, whichever page the link opens. Before the first
// render, and never part of it: hydration is unaffected.
captureLink(window.location.search);

// src/entry-prerender.tsx renders this same tree at build time; keep the two in step.
const app = (
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
        {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>
);

// The first render waits for the visitor's language: Turkish and English are bundled and ready at
// once, any other is a small chunk (src/i18n). Public pages arrive prerendered
// (scripts/prerender.mjs); hydrate one only when this visitor's first render reproduces it,
// otherwise start from an empty root, as without prerendering.
void i18nReady.then(() => {
  if (canHydrate(container)) {
    hydrateRoot(container, app);
  } else {
    container.textContent = '';
    createRoot(container).render(app);
  }
  revealPrerender();
});
